import { Fragment, useState, useContext, useEffect } from 'react';
import { Modal, Button, ListGroup, ListGroupItem, ButtonGroup, Table, FormControl } from 'react-bootstrap'

// For Login related items
import { AuthContext } from "./Login/AuthContext"
import { DatabaseContext } from './DatabaseContext';
import { API_BASE } from '../services/apiService';

// Common interface items
import Icons from './common/Icons';
import ProtectedComponent from './common/ProtectedComponent';
import { getPrettyBkgColor, isDef, renderIfNotEmpty, dateYYYYMMMDD, dateYYYYMMMDDHHMM, boolToIcon, validateDecimal, noCheck, validateNonNegativeInt } from '../services/commonParse';
import { SimpleFold, GeometryCard } from './common/misc';
// Interaction with database
import { addBP, updateBP, deleteBP, validateCMSbarcode, validateBareBarcode, validateIdentifier, validateWS, baseplateGeometryDisplay, createXMLnewparts, createXMLqcgrade, makeBareTypeOtions } from '../services/baseplateService';
import { makeMaterialOptions, materialDescription } from '../services/materialService';
import { makeKaptonOptions, updateKapton, kaptonDescription } from '../services/kaptonService';
import { makeGlueOptions, glueDescription } from '../services/glueService';
import { makeTapeOptions, updateTape, tapeDescription } from '../services/tapeService';
import { makeShipmentOptions } from '../services/shipmentService';
import { userOptions } from '../services/userService';
// Additional interface items
import { HGCFormRowText, HGCFormRowDate, HGCFormRowSelect, HGCFormRowBool } from './common/SimpleForm';
import TableViewAdd, { defaultAdd, defaultResetAdd, defaultValidateAdd } from './common/TableViewAdd';
import TableViewEdit, { defaultEdit, defaultReset, defaultValidateEdit } from './common/TableViewEdit';
import { BatchTable, BatchEditButton, createDefaultTable, BatchEditActions, ResetFormCommon, ResetTabContent } from './common/BatchEdit';
import PaginateTable, { CategoryFilter, FuzzyTextFilter } from './common/TableDisplay';
import { findByUnique, findTableRow } from '../services/tableUtils';
import "./modal-xxl.css" // Loading the style required for wider CSS

// import images
import F0W from "../images/baseplate/F0W.png"
import TLW from "../images/baseplate/TLW.png"
import BLW from "../images/baseplate/BLW.png"
import SLW from "../images/baseplate/SLW.png"
import FiveLW from "../images/baseplate/5LW.png"
import SHW from "../images/baseplate/SHW.png"
import BHW from "../images/baseplate/BHW.png"

import F0T from "../images/baseplate/F0T.png"
import TLT from "../images/baseplate/TLT.png"
import BLT from "../images/baseplate/BLT.png"
import LLT from "../images/baseplate/LLT.png"
import RLT from "../images/baseplate/RLT.png"
import FiveLT from "../images/baseplate/5LT.png"
import THT from "../images/baseplate/THT.png"

const SimpleTable = ({ children }) => {
  return <Table><tbody>{children}</tbody></Table>
}

const SimpleTableRow = ({ label, label_width, content_width, bkgcolor, children }) => {
  const lwidth = label_width ? label_width : 0;
  const cwidth = content_width ? content_width : 0;
  return <tr>
    <td style={{ backgroundColor: bkgcolor, minWidth: lwidth, textAlign: "right" }}>{label}</td>
    <td style={{ backgroundColor: bkgcolor, minWidth: cwidth, textAlign: "left" }}>{children}    </td>
  </tr>
}


/// Definition of the actual columns
const BaseplateColumns = () => [
  {
    Header: 'Geometry',
    Cell: (bp) => <Fragment>{baseplateGeometryDisplay(bp.type)} ({bp.type})</Fragment>,
    Filter: CategoryFilter((bp) => bp.type),
  },
  {
    Header: 'Identifier',
    CellStyle: styleCellID,
    Cell: CellIDContent,
    Filter: FuzzyTextFilter((bp) => [bp.bar_code, bp.bare_barcode, String(bp.workshop_serial).padStart(6, "0")])
  },
  {
    Header: 'Geometry Grade',
    CellStyle: styleCell2DQC,
    Cell: renderCell2DQC,
    Filter: CategoryFilter((bp) => bp.tolerance_grade)
  },
  {
    Header: 'Flatness [mm]',
    CellStyle: styleCellFlatness,
    Cell: BPCellFlatness,
    Filter: CategoryFilter((bp) => bp.flatness_grade)
  },
  {
    Header: 'Manual',
    CellStyle: styleCellManual,
    Cell: BPCellManual,
    Filter: CategoryFilter((bp) => bp.material_id)
  },
  {
    Header: 'Kapton',
    CellStyle: styleCellKapton,
    Cell: BPCellKapton,
    Filter: CategoryFilter((bp) => kaptonBkgColor(bp) ? kaptonBkgColor(bp) : 'Not Laminated')
  },
  {
    Header: 'Tape',
    CellStyle: styleCellTape,
    Cell: BPCellTape,
    Filter: CategoryFilter((bp) => bp.tape_id ? "Taped" : "Not Taped")
  },
  {
    Header: "Shipment",
    CellStyle: styleCellShipment,
    Cell: BPCellShipment
  },
  {
    Header: 'Files',
    Cell: renderCellFiles
  }
]

// For identifier
const styleCellID = (bp) => ({
  backgroundColor: getPrettyBkgColor(bp.bar_code ? 'green' : ''),
})

const CellIDContent = (bp) => {
  const { user } = useContext(AuthContext);
  const id = bp.id;
  const color = getPrettyBkgColor(bp.bar_code ? 'green' : '');
  const { updateBaseplateList } = useContext(DatabaseContext);

  const [showEdit, setShowEdit] = useState(false);
  const [showDetailEdit, setShowDetailEdit] = useState(false);

  const deleteAction = async () => { await deleteBP(id); updateBaseplateList(); }

  return (
    <Fragment>
      <SimpleTable>
        <SimpleTableRow label="CMS bar code" label_width="4.5em" content_width="9.5em" bkgcolor={color}>{bp.bar_code}</SimpleTableRow>
        <SimpleTableRow label="bare code" label_width="4.5em" content_width="9.5em" bkgcolor={color} >{bp.bare_barcode}</SimpleTableRow>
        <SimpleTableRow label="Serial" label_width="4.5em" content_width="9.5em" bkgcolor={color} >{bp.workshop_serial}</SimpleTableRow>
      </SimpleTable>
      <span style={{ textAlign: "right", width: "100%", display: "inline-block" }}>
        <ButtonGroup aria-label="Bp actions">
          <Button variant='light' size='sm' onClick={() => setShowEdit(true)}>{Icons.edit}</Button>
          {user.role === "admin" && <Button variant='light' size='sm' onClick={() => setShowDetailEdit(true)}>{Icons.info}</Button>}
          {user.role === "admin" && <Button variant='light' size='sm' onClick={deleteAction}>{Icons.remove}</Button>}
        </ButtonGroup>
      </span>
      {showEdit && <BaseplateEdit id={id} origData={bp} showState={[showEdit, setShowEdit]} />}
      {showDetailEdit && <BaseplateJSONEdit id={id} origData={bp} showState={[showDetailEdit, setShowDetailEdit]} />}
    </Fragment >
  );
}

// For 2D QC information
const styleCell2DQC = (bp) => ({
  backgroundColor: isDef(bp.tolerance_grade) && getPrettyBkgColor(bp.tolerance_grade),
})
const renderCell2DQC = (bp) => {
  // Making a cell item for measurement
  const ColoredCell = ({ colName }) => {
    const bkgColor = getPrettyBkgColor(bp[`${colName}_grade`])
    const content = bp[colName]
    return <ListGroupItem style={{ backgroundColor: bkgColor }}>{content}</ListGroupItem>;
  }

  // For list of measurements
  const ListRow = ({ label, colName, length }) => {
    const list = []
    for (let i = 0; i < length; i++) {
      list.push(<ColoredCell colName={`${colName}${i}`} key={`key-${colName}${i}`} />);
    }

    const content = <ListGroup horizontal="sm">{list.map((x) => x)}</ListGroup>
    return <SimpleTableRow label={label}>{content}</SimpleTableRow>
  }

  // For single item items
  const SingleRow = ({ label, colName }) => {
    return <SimpleTableRow label={label}>
      <ListGroup horizontal="sm"><ColoredCell colName={colName} /></ListGroup>
    </SimpleTableRow>
  }

  // For boolean itesm
  const BoolItem = ({ label, colName }) => {
    return (<ListGroupItem>
      {label}{boolToIcon(bp[colName])}
    </ListGroupItem>);
  }

  return (<Fragment>
    <span style={{ display: "inline-block", width: "100%", textAlign: "center" }}>
      {renderIfNotEmpty(bp.preqc_user)}<br />
      {dateYYYYMMMDDHHMM(bp.preqc_date)}
    </span>
    <SimpleFold>
      <SimpleTable>
        <ListRow label="Width" colName="width_edge" length={3} />
        <ListRow label="Corners" colName="width_corner" length={3} />
        <ListRow label="Radius" colName="radius" length={6} />
        <ListRow label="Tab width" colName="tab_width" length={3} />
        <ListRow label="Tab height" colName="tab_height" length={3} />
        <SingleRow label="Dist. hole" colName="dist_hole_slot" />
        <SingleRow label="Len. hole" colName="length_slot" />
        <SimpleTableRow label="Checks">
          <ListGroup horizontal="sm">
            <BoolItem label="Hole size" colName="diameter_hole_passed" />
            <BoolItem label="Slot width" colName="width_slot_passed" />
            <BoolItem label="Notches" colName="notch_size_passed" />
          </ListGroup>
        </SimpleTableRow>
      </SimpleTable>
    </SimpleFold>
  </Fragment >);
}

// For flatness
const getFlatGrade = (bp) => {
  return (bp.flatness_grade ? bp.flatness_grade :
    bp.flatness_lam_grade ? bp.flatness_lam_grade :
      bp.flatness_bare_grade ? bp.flatness_bare_grade : null);
}
const styleCellFlatness = (bp) => ({
  backgroundColor: getPrettyBkgColor(getFlatGrade(bp))
})
const BPCellFlatness = (bp) => {
  const has_lr = (bp.flatness_bare_l != null && bp.flatness_bare_r != null);
  const no_bare = (bp.flatness_bare_l == null && bp.flatness_bare_r == null);

  const list_left = [];
  const list_right = [];
  const list_lam = [];
  for (let i = 0; i < 10; i++) {
    list_left.push(<ListGroupItem key={`height_bare${i}`}>{bp[`height_bare${i}`]}</ListGroupItem>);
    list_right.push(<ListGroupItem key={`height_bare${i + 10}`}>{bp[`height_bare${i + 10}`]}</ListGroupItem>);
    list_lam.push(<ListGroupItem key={`height_lam${i}`}>{bp[`height_lam${i}`]}</ListGroupItem>);
  }
  while (list_left[list_left.length - 1] === null) list_left.pop();

  const bare_string = (has_lr ? bp.flatness_bare_l + "/" + bp.flatness_bare_r :
    no_bare ? "--" : String(Number(bp.flatness_bare_l) + Number(bp.flatness_bare_r)));
  const color = getPrettyBkgColor(getFlatGrade(bp))
  return (<Fragment>
    <SimpleTable>
      <SimpleTableRow bkgcolor={color} label="Lam.">{bp.flatness_lam}</SimpleTableRow>
      <SimpleTableRow bkgcolor={color} label={has_lr ? "Bare L/R" : "Bare"}>{bare_string}</SimpleTableRow>
    </SimpleTable>
    <SimpleFold>
      <SimpleTable>
        <SimpleTableRow label="Bare measured">{dateYYYYMMMDDHHMM(bp.preqc_date)} by {renderIfNotEmpty(bp.preqc_user)}</SimpleTableRow>
        <SimpleTableRow label="Lam. measured">{dateYYYYMMMDDHHMM(bp.postqc_date)} by {renderIfNotEmpty(bp.postqc_user)}</SimpleTableRow>
      </SimpleTable>
      <SimpleTable>
        <tr>
          <td>Bare L</td>
          <td>Bare R</td>
          <td>Lam.</td>
        </tr>
        <tr>
          <td> {list_left}</td>
          <td> {list_right}</td>
          <td> {list_lam}</td>
        </tr>
      </SimpleTable>
    </SimpleFold>
  </Fragment>);
}

// Manual meausrements
const styleCellManual = (bp) => {
  return {
    backgroundColor: getPrettyBkgColor(bp.weight_lam ? 'green' : ''),
  }
}

const BPCellManual = (bp) => {
  const { materialList } = useContext(DatabaseContext);
  const color = getPrettyBkgColor(bp.weight_lam ? 'green' : '');
  return <SimpleTable>
    <SimpleTableRow bkgcolor={color} label="Bare weight [g]">{bp.weight_bare}</SimpleTableRow>
    <SimpleTableRow bkgcolor={color} label="Lam. weight [g]">{bp.weight_lam}</SimpleTableRow>
    <SimpleTableRow bkgcolor={color} label="Thick. [mm]">{bp.thickness}</SimpleTableRow>
    <SimpleTableRow bkgcolor={color} content_width="12em" label="Material">{materialDescription(materialList, bp.material_id)}</SimpleTableRow>
  </SimpleTable>
    ;
}

// Kapton quality checks
const kaptonBkgColor = (bp) => {
  const aligned = bp.kapton_align_passed;
  const hasDefect = (bp.defect_lam_delam_pad > 0) || (bp.defect_lam_glue_pad > 0) || (bp.defect_lam_glue_hole > 0);

  return aligned === null ? '' :
    aligned === false ? 'red' :
      hasDefect ? 'orange' :
        'green';
}

const styleCellKapton = (bp) => {
  return {
    backgroundColor: getPrettyBkgColor(kaptonBkgColor(bp)),
    textAlign: 'center'
  }
}
const BPCellKapton = (bp) => {
  const { kaptonList, glueList } = useContext(DatabaseContext);
  const aligned = bp.kapton_align_passed;
  const color = getPrettyBkgColor(kaptonBkgColor(bp));

  return (<Fragment>
    <SimpleTable>
      <SimpleTableRow bkgcolor={color} label="Kapton">{kaptonDescription(kaptonList, bp.kapton_id)}</SimpleTableRow>
      <SimpleTableRow bkgcolor={color} label="Aligned">{boolToIcon(aligned)}</SimpleTableRow>
      <SimpleTableRow bkgcolor={color} label="Lam. user">{renderIfNotEmpty(bp.lam_user)}/{renderIfNotEmpty(bp.lam_user2)}</SimpleTableRow>
      <SimpleTableRow bkgcolor={color} label="lam. date">{dateYYYYMMMDD(bp.lam_date)}</SimpleTableRow>
    </SimpleTable>
    <SimpleFold>
      <SimpleTable>
        <SimpleTableRow label="Glue">{glueDescription(glueList, bp.glue_id)}</SimpleTableRow>
        <SimpleTableRow label="Glue Amount">{renderIfNotEmpty(bp.glue_mass)}(g)</SimpleTableRow>
        <SimpleTableRow label="Surface prep. time">{renderIfNotEmpty(bp.surface_preptime)}(sec)</SimpleTableRow>
        <SimpleTableRow label="Insp. users">{renderIfNotEmpty(bp.inspect_user1)}/{renderIfNotEmpty(bp.inspect_user2)}</SimpleTableRow>
        <SimpleTableRow label="Delam. pad">{renderIfNotEmpty(bp.defect_lam_delam_pad)}</SimpleTableRow>
        <SimpleTableRow label="Glue on pad">{renderIfNotEmpty(bp.defect_lam_glue_pad)}</SimpleTableRow>
        <SimpleTableRow label="Glue in hole">{renderIfNotEmpty(bp.defect_lam_glue_hole)}</SimpleTableRow>
        {bp.recovery_date &&
          <Fragment>
            <SimpleTableRow label="Rec. date">{dateYYYYMMMDD(bp.recovery_date)}</SimpleTableRow>
            <SimpleTableRow label="Rec. users">{renderIfNotEmpty(bp.recovery_user1)}/{renderIfNotEmpty(bp.recovery_user2)}</SimpleTableRow>
          </Fragment>
        }
      </SimpleTable>
    </SimpleFold>
  </Fragment>);
}

// Tape checks
const styleCellTape = (bp) => ({
  backgroundColor: getPrettyBkgColor(bp.tape_id ? 'green' : ''),
})
const BPCellTape = (bp) => {
  const { tapeList } = useContext(DatabaseContext);
  const color = getPrettyBkgColor(bp.tape_id ? 'green' : '')
  return <SimpleTable>
    <SimpleTableRow bkgcolor={color} label="Tape order">{tapeDescription(tapeList, bp.tape_id)}</SimpleTableRow>
    <SimpleTableRow bkgcolor={color} label="Tape user">{renderIfNotEmpty(bp.tape_user1)}/{renderIfNotEmpty(bp.tape_user2)}</SimpleTableRow>
    <SimpleTableRow bkgcolor={color} label="Tape date">{dateYYYYMMMDD(bp.tape_date)}</SimpleTableRow>
  </SimpleTable>
}

// Shipment related
const styleCellShipment = (bp) => ({
  backgroundColor: getPrettyBkgColor(bp.shipment_id ? "green" : '')
})

const BPCellShipment = (bp) => {
  const { shipmentList } = useContext(DatabaseContext);
  const shipment = bp.shipment_id === null ? null : findByUnique(shipmentList, "id", bp.shipment_id);
  const color = getPrettyBkgColor(bp.shipment_id ? "green" : '')
  return <SimpleTable>
    <SimpleTableRow bkgcolor={color} label="Shipped on:">{dateYYYYMMMDD(shipment ? shipment.ship_date : null)}</SimpleTableRow>
    <SimpleTableRow bkgcolor={color} label="Shipped to">{renderIfNotEmpty(shipment ? shipment.destination : null)}</SimpleTableRow>
    <SimpleTableRow bkgcolor={color} label="Comments:">{bp.comment}</SimpleTableRow>
    <SimpleTableRow bkgcolor={color} label="Part in CMSR:">
      {boolToIcon(bp.is_db_central ? bp.is_db_central : null)}
      {bp.bar_code && <Button style={{ marginLeft: "2em" }} onClick={() => createXMLnewparts([bp.id,], String(bp.id))}>{Icons.down}</Button>}
    </SimpleTableRow>
    <SimpleTableRow bkgcolor={color} label="QC in CMSR:">
      {boolToIcon(bp.is_db_central_qc ? bp.is_db_central : null)}
      {bp.bar_code && <Button style={{ marginLeft: "2em" }} onClick={() => createXMLqcgrade([bp.id,], String(bp.id))}>{Icons.down}</Button>}
    </SimpleTableRow>
  </SimpleTable>
}

// Associated File related
const renderCellFiles = (bp) => {
  const files = bp.backup_files === null ? [] : bp.backup_files;
  if (files.length === 0) { return <></> }
  return (
    <SimpleFold>
      <ul>
        {files.map((item, i) => (
          <li key={`link${i}`}>
            <a href={`${API_BASE}/hgcal-img/backup/${item}`} download>{item}</a>
          </li>
        ))}
      </ul>
    </SimpleFold>
  );
}

const BaseplateEdit = ({ id, origData, showState }) => {
  const [, setShow] = showState;
  const { baseplateList, updateBaseplateList, materialList, kaptonList, glueList, tapeList, shipmentList, userList } = useContext(DatabaseContext);
  const [invalid, setInvalid] = useState("");

  // Creating all items related to the display form
  const form = {
    'bar_code': useState(origData.bar_code),
    'bare_barcode': useState(origData.bare_barcode),
    'weight_bare': useState(origData.weight_bare),
    'thickness': useState(origData.thickness),
    'material_id': useState(origData.material_id),
    "diameter_hole_passed": useState(origData.diameter_hole_passed),
    "width_slot_passed": useState(origData.width_slot_passed),
    "tolerance_grade": useState(origData.tolerance_grade), // Unlike adding, we will be explicitly setting this
    'kapton_id': useState(origData.kapton_id),
    'glue_id': useState(origData.glue_id),
    'weight_lam': useState(origData.weight_lam),
    'kapton_align_passed': useState(origData.kapton_align_passed),
    'lam_user': useState(origData.lam_user),
    'lam_user2': useState(origData.lam_user2),
    'inspect_user1': useState(origData.lam_user),
    'inspect_user2': useState(origData.lam_user2),
    "defect_lam_delam_pad": useState(origData.defect_lam_delam_pad),
    "defect_lam_glue_pad": useState(origData.defect_lam_glue_pad),
    "defect_lam_glue_hole": useState(origData.defect_lam_glue_hole),
    'tape_id': useState(origData.tape_id),
    'shipment_id': useState(origData.shipment_id),
    'comment': useState(origData.comment)
  }
  // eslint-disable-next-line
  const error = {
    'bar_code': useState(""),
    'bare_barcode': useState(""),
    'weight_bare': useState(""),
    'thickness': useState(""),
    'defect_lam_delam_pad': useState(""),
    'defect_lam_glue_pad': useState(""),
    'defect_lam_glue_hole': useState(""),
    'weight_lam': useState(""),
    "comment": useState("")
  }

  const resetAction = defaultReset(form, origData);
  const editAction = defaultEdit(id, form, updateBP, updateBaseplateList, setShow);
  useEffect(() => { setInvalid(defaultValidateEdit(error)) }, [error]);

  const title = origData.bar_code ? origData.bar_code : origData.workshop_serial;
  return <TableViewEdit title={`Edit Baseplate [${id}][${title}]`} showState={showState} resetAction={resetAction} editAction={editAction} invalid={invalid} key={id}>
    <HGCFormRowText label="BAREPLATE CMS BAR CODE" dataState={form["bare_barcode"]} errorState={error["bare_barcode"]} validate={validateBareBarcode(id, baseplateList)} />
    <HGCFormRowText label="WEIGHT BARE [g]" dataState={form["weight_bare"]} errorState={error["weight_bare"]} validate={validateDecimal()} />
    <HGCFormRowText label="THICKNESS [mm]" dataState={form["thickness"]} errorState={error["thickness"]} validate={validateDecimal()} />
    <HGCFormRowSelect label="MATERIAL" dataState={form["material_id"]} options={makeMaterialOptions(materialList)} />
    <HGCFormRowBool label="HOLE DIAMETER" dataState={form["diameter_hole_passed"]} />
    <HGCFormRowBool label="SLOT DIAMETER" dataState={form["width_slot_passed"]} />
    <HGCFormRowSelect label="TOLERANCE GRADE" dataState={form["tolerance_grade"]} options={[
      { value: "green", "label": "Green (Grade A)" },
      { value: "orange", "label": "Orange (Grade B)" },
      { value: "red", "label": "Red (Grade C)" }
    ]} />
    <hr />
    <HGCFormRowSelect label="KAPTON" dataState={form["kapton_id"]} options={makeKaptonOptions(kaptonList, origData.type)} />
    <HGCFormRowSelect label="GLUE" dataState={form["glue_id"]} options={makeGlueOptions(glueList, false, false)} />
    <HGCFormRowText label="WEIGHT LAMINATED [g]" dataState={form["weight_lam"]} errorState={error["weight_lam"]} validate={validateDecimal()} />
    <HGCFormRowSelect label="LAM. USER" dataState={form["lam_user"]} options={userOptions(userList)} />
    <HGCFormRowSelect label="LAM. USER (2)" dataState={form["lam_user2"]} options={userOptions(userList)} />
    <HGCFormRowSelect label="INSP. USER" dataState={form["inspect_user1"]} options={userOptions(userList)} />
    <HGCFormRowSelect label="INSP. USER (2)" dataState={form["inspect_user2"]} options={userOptions(userList)} />
    <HGCFormRowText label="DEFECT: DELAM PAD" dataState={form["defect_lam_delam_pad"]} errorState={error["defect_lam_delam_pad"]} validate={validateNonNegativeInt()} />
    <HGCFormRowText label="DEFECT: GLUE ON PAD" dataState={form["defect_lam_glue_pad"]} errorState={error["defect_lam_glue_pad"]} validate={validateNonNegativeInt()} />
    <HGCFormRowText label="DEFECT: GLUE IN HOLE" dataState={form["defect_lam_glue_hole"]} errorState={error["defect_lam_glue_hole"]} validate={validateNonNegativeInt()} />
    <HGCFormRowBool label="KAPTON ALIGNED" dataState={form["kapton_align_passed"]} />
    <hr />
    <HGCFormRowSelect label="TAPE" dataState={form["tape_id"]} options={makeTapeOptions(tapeList, origData.type)} />
    <HGCFormRowText label="CMS BAR CODE" dataState={form["bar_code"]} errorState={error["bar_code"]} validate={validateCMSbarcode(id, baseplateList)} />
    <hr />
    <HGCFormRowSelect label="SHIPMENT" dataState={form["shipment_id"]} options={makeShipmentOptions(shipmentList)} />
    <HGCFormRowText label="COMMENT" dataState={form["comment"]} errorState={error["comment"]} validate={noCheck} />
  </TableViewEdit>
}

const BaseplateJSONEdit = ({ id, origData, showState }) => {
  const [, setShow] = showState;
  const { updateBaseplateList } = useContext(DatabaseContext)

  const createEdit = () => {
    const editObj = { ...origData }; // Must make a copy!! Never overwrite
    // The following are fields that should never be edit
    delete editObj.id; // This should not be editable
    // Removing the backup files item
    delete editObj.backup_files;
    // Deleting items used for interface generation
    delete editObj.score;
    return JSON.stringify(editObj, null, 4);
  }

  const [edit, setEdit] = useState(createEdit());
  const resetAction = () => { setEdit(createEdit()); }

  const editAction = async () => {
    const editObj = JSON.parse(edit);
    await updateBP(id, editObj);
    setShow(false);
    updateBaseplateList();
  }

  return <TableViewEdit title={`Editing content of baseplate@[${id}]`} resetAction={resetAction} editAction={editAction} showState={showState} invalid={""} key={`detail_${id}`}>
    <FormControl
      text="text"
      as="textarea"
      value={edit}
      rows={30}
      style={{ width: "40em" }}
      onChange={(e) => { setEdit(e.target.value) }}
    />
  </TableViewEdit >
}

const GeometryReference = () => {
  return <>
    <SimpleFold header="CuW Geometry Reference">
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <GeometryCard src={F0W} bodytext={"F0W (CuW/CEE Full)"} />
        <GeometryCard src={FiveLW} bodytext={"5LW (CuW/CEE LD Five)"} />
        <GeometryCard src={SLW} bodytext={"SLW (CuW/CEE LD Semi)"} />
        <GeometryCard src={BLW} bodytext={"BLW (CuW/CEE LD Bottom)"} />
        <GeometryCard src={TLW} bodytext={"TLW (CuW/CEE LD Top)"} />
        <GeometryCard src={BHW} bodytext={"BHW (CuW/CEE HD Bottom)"} />
        <GeometryCard src={SHW} bodytext={"SHW (CuW/CEE HD Semi)"} />
      </div >
    </SimpleFold>
    <SimpleFold header="Ti Geometry Reference">
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <GeometryCard src={F0T} bodytext={"F0T (Ti/CEH Full)"} />
        <GeometryCard src={LLT} bodytext={"LLT (CuW/CEH LD Left)"} />
        <GeometryCard src={TLT} bodytext={"TLT (CuW/CEH LD Top)"} />
        <GeometryCard src={FiveLT} bodytext={"5LT (CuW/CEH LD Five)"} />
        <GeometryCard src={RLT} bodytext={"RLT (CuW/CEH LD Right)"} />
        <GeometryCard src={BLT} bodytext={"BLT (CuW/CEH LD Bottom)"} />
        <GeometryCard src={THT} bodytext={"THT (CuW/CEH HD Top)"} />
      </div >
    </SimpleFold>
  </>
}

const BaseplateAdd = ({ showState }) => {
  const { baseplateList, materialList, updateBaseplateList } = useContext(DatabaseContext);
  const [, setShow] = showState;
  // eslint-disable-next-line
  const form = {
    // The first 2 are explicitly not exposed to the user
    'workshop_serial': useState(null),
    'bare_barcode': useState(null),
    'weight_bare': useState(null),
    "thickness": useState(null),
    "material_id": useState(null),
    "type": useState(null),
    "diameter_hole_passed": useState(null),
    "width_slot_passed": useState(null),
    "tolerance_grade": useState(null),
    "comment": useState(null),
  }
  // eslint-disable-next-line
  const error = {
    'workshop_serial': useState(""),
    'bare_barcode': useState(""),
    'weight_bare': useState(""),
    "thickness": useState(""),
    "material_id": useState(""),
    "type": useState(""),
    "diameter_hole_passed": useState(""),
    "width_slot_passed": useState(""),
    "comment": useState(""),
  };
  const [invalid, setInvalid] = useState("");

  const resetAction = defaultResetAdd(form)
  const addAction = defaultAdd(form, addBP, updateBaseplateList, resetAction, setShow);
  useEffect(() => {
    const inval = defaultValidateAdd(error);
    if (inval !== "") {
      setInvalid(inval);
      return;
    }

    const mat_id = form.material_id[0];
    const ptype = form.type[0];
    if (mat_id !== null) {
      const matinfo = findTableRow(materialList, (mat) => mat.id === mat_id);
      if ((matinfo.type === "Ti" && ptype[2] !== "T") || (matinfo.type === "CuW" && ptype[2] !== "W")) {
        setInvalid(`Plate geometry [${ptype}] and material type [${matinfo.type}] is mismatched!`)
        return;
      }
    }

    const ws = form.workshop_serial[0];
    const bare = form.bare_barcode[0]
    if ((ws === null || ws === "") && (bare === null || bare === "")) {
      setInvalid("Need to specify either [WORKSHOP SERIAL] or [BARE CMS BARECODE]")
    } else {
      setInvalid("")
    }
  }, [error, form, materialList]);

  useEffect(() => { // Updating the tolerance grade
    const target_grade = (form["diameter_hole_passed"][0] === false || form["width_slot_passed"][0] === false) ? "red" : null;
    form["tolerance_grade"][1](target_grade);
  }, [form])

  return <TableViewAdd title="Add new baseplate" showState={showState} addAction={addAction} invalid={invalid}>
    <HGCFormRowText label="WORKSHOP SERIAL" dataState={form["workshop_serial"]} errorState={error["workshop_serial"]} validate={validateWS(null, baseplateList)} autoFocus={true} />
    <HGCFormRowText label="BAREPLATE CMS BAR CODE" dataState={form["bare_barcode"]} errorState={error["bare_barcode"]} validate={validateBareBarcode(null, baseplateList)} />
    <HGCFormRowText label="WEIGHT BARE [g]" dataState={form["weight_bare"]} errorState={error["weight_bare"]} validate={validateDecimal()} />
    <HGCFormRowText label="THICKNESS [mm]" dataState={form["thickness"]} errorState={error["thickness"]} validate={validateDecimal()} />
    <HGCFormRowSelect label="MATERIAL" dataState={form["material_id"]} errorState={error["material_id"]} options={makeMaterialOptions(materialList, true)} allowNull={false} />
    <HGCFormRowSelect label="GEOMETRY" dataState={form["type"]} errorState={error["type"]} options={makeBareTypeOtions()} allowNull={false} />
    <HGCFormRowBool label="HOLE DIAMETER" dataState={form["diameter_hole_passed"]} errorState={error["diameter_hole_passed"]} allowNull={false} />
    <HGCFormRowBool label="SLOT DIAMETER" dataState={form["width_slot_passed"]} errorState={error["width_slot_passed"]} allowNull={false} />
    <HGCFormRowText label="COMMENT" dataState={form["comment"]} errorState={error["comment"]} validate={noCheck} />
    <GeometryReference />
  </TableViewAdd>
}

const BaseplateMultiAdd = ({ showState }) => {
  const [show, setShow] = showState;
  const [showAction, setShowAction] = useState(false);
  const [running, setRunning] = useState(false);
  const { baseplateList, materialList, updateBaseplateList } = useContext(DatabaseContext);

  // eslint-disable-next-line
  const formCommon = {
    "material_id": useState(null),
    "type": useState(null),
  }
  // eslint-disable-next-line
  const errorCommon = {
    "material_id": useState(""),
    "type": useState(""),
  }

  const validateNewID = (idstr) => {
    return idstr.startsWith("320BP") ? validateBareBarcode(null, baseplateList)(idstr) : validateWS(null, baseplateList)(idstr)
  }

  const ColumnFormat = [
    { title: "BASEPLATE ID", target: "identifier", type: "text", validate: validateNewID },
    { title: "WEIGHT BARE [g]", target: "weight_bare", type: "text", validate: validateDecimal() },
    { title: "THICKNESS [mm]", target: "thickness", type: "text", validate: validateDecimal() },
    { title: "HOLE DIAMETER", target: "diameter_hole_passed", type: "bool" },
    { title: "SLOT DIAMETER", target: "width_slot_passed", type: "bool" },
  ]
  const tabDataState = useState(createDefaultTable(ColumnFormat, 20, null));
  const tabErrState = useState(createDefaultTable(ColumnFormat, 20, ""));

  const [invalid, setInvalid] = useState("");
  useEffect(() => {
    const inval = defaultBatchInvalid(errorCommon, tabErrState[0]);
    if (inval !== "") {
      setInvalid(inval);
      return;
    }

    const mat_id = formCommon.material_id[0];
    const ptype = formCommon.type[0];
    if (mat_id !== null) {
      const matinfo = findTableRow(materialList, (mat) => mat.id === mat_id);
      if ((matinfo.type === "Ti" && ptype[2] !== "T") || (matinfo.type === "CuW" && ptype[2] !== "W")) {
        setInvalid(`Plate geometry [${ptype}] and material type [${matinfo.type}] is mismatched!`)
        return;
      }
    }
    setInvalid("");
  }, [errorCommon, tabErrState, formCommon, materialList]);

  const actionList = [
    { "idList": useState([]), "headStyle": { color: "red" }, "parse": (form) => form["tolerance_grade"] === "red", "description": "Out-of-Tolerance! Move to \"NG\" box in Cabinet 3.b" },
    { "idList": useState([]), "headStyle": { color: "darkgreen" }, "parse": () => true, "description": "Added plates! Move to \"BARE METAL (ALL)\" box at Keyence Station" }
  ]
  const [skip, setSkip] = useState({});

  const RunAdd = async () => {
    setRunning(true);
    const skipEntries = {}
    const newEntries = {}
    tabDataState[0].forEach((row, rowIdx) => {
      if (!row.identifier) return; // Early exist if identifier is blank
      const createForm = {}
      if (row.identifier.startsWith("320BP")) {
        createForm["bare_barcode"] = row.identifier;
      } else {
        createForm["workshop_serial"] = row.identifier
      }

      // Creating the new items list
      let bp_error = ""
      for (const key of Object.keys(row)) {
        if (row[key] === null || row[key] === undefined) { bp_error = `Has empty entry at column [${key}]`; break; }
        if (key === "identifier") continue;
        createForm[key] = row[key]; // Pushing the entry into the baseplate
      }
      for (const key of Object.keys(formCommon)) {
        createForm[key] = formCommon[key][0];
      }

      // An additional parser
      createForm["tolerance_grade"] = createForm["width_slot_passed"] === false || createForm["diameter_hole_passed"] === false ? "red" : null;

      // bp_error
      if (bp_error === "") {
        addBP(createForm);
        newEntries[row.identifier] = [null, createForm];
      } else {
        skipEntries[row.identifier] = bp_error;
      }
    })
    setRunning(false);
    await updateBaseplateList();

    // Post Actions
    setSkip(skipEntries);
    ResetFormCommon(formCommon)
    ResetTabContent(tabDataState, ColumnFormat, null)
    SortActionList(actionList, newEntries);
    setShow(false);
    setShowAction(true);
  }

  return <Fragment>
    <Modal show={show} onHide={() => setShow(false)} animation="false" size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Report Tape Result</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <HGCFormRowSelect label="MATERIAL" dataState={formCommon["material_id"]} errorState={errorCommon["material_id"]} options={makeMaterialOptions(materialList, true)} allowNull={false} autoFocus={true} />
        <HGCFormRowSelect label="GEOMETRY" dataState={formCommon["type"]} errorState={errorCommon["type"]} options={makeBareTypeOtions()} allowNull={false} autoFocus={true} />
        <GeometryReference />
        <br />
        <BatchTable header={ColumnFormat} dataState={tabDataState} errorState={tabErrState} />
        <br />
      </Modal.Body>
      <Modal.Footer>
        <BatchEditButton invalid={invalid} action={RunAdd} running={running} />
      </Modal.Footer>
    </Modal>
    <BatchEditActions title="New plates added!" showState={[showAction, setShowAction]} actionList={actionList} skipEntries={skip} />
  </Fragment >
}


const BaseplateQuickCheck = () => {
  const { baseplateList } = useContext(DatabaseContext);
  const [data, setData] = useState("");
  const [error, setError] = useState("");

  const validateTape = (ws) => {
    if (ws === "") return "";
    const bp = findTableRow(baseplateList, (bp) => Number(bp.workshop_serial) === Number(ws) || bp.bare_barcode === ws || bp.bar_code === ws);
    if (bp === null || bp === undefined) return "Baseplate not found"
    // Still checking all the other items for the lamination in case it was missed
    if (bp.weight_bare === null && bp.thickness === null) return "Missing manual measurements";
    if (bp.flatness_bare === null && bp.height_bare0 === null) return "Missing bare height measurements";
    if (bp.radius0 === null) return "Missing 2D measurements";
    if (bp.flatness_grade === "red") return "Red-grade flatness";
    if (bp.tolerance_grade === "red") return "Red-grade 2D measurements";
    if (bp.kapton_id === null) return "[READY] for Kapton"
    // Additional measurements that should be done after kapton lamination
    if (bp.kapton_align_passed !== true) return "Kapton not aligned";
    if (bp.defect_lam_delam_pad !== 0 || bp.defect_lam_glue_hole !== 0 || bp.defect_lam_glue_pad !== 0) return "Has lamination defects";
    if (bp.weight_lam === null) return "Missing manual measurements";
    if (bp.flatness_lam === null || bp.height_lam0 === null) return "Missing laminated height measurements";
    if (bp.flatness_lam_grade === "red") return "Red-grade laminated flatness";
    if (bp.tape_id === null) return "[READY] for tape";
    // Additional cheks for shipment preparation
    if (bp.bar_code === null) return "Missing CMS barcode";
    if (bp.shipmend_id === null) return "[READY] for shipment";
    return "[READY] All done!"
  }

  return <Fragment>
    <div>
      <div>Quick Check</div>
      <div>
        <FormControl
          text="text"
          value={data}
          placeholder="Serial or '320BP' code or '320BA' code"
          onChange={(e) => { setData(e.target.value); setError(validateTape(e.target.value)); }}
          isInvalid={error !== "" && !error.startsWith("[READY]")}
          isValid={error.startsWith("[READY]")}
        />
        <FormControl.Feedback type={error.startsWith("[READY]") ? "valid" : "invalid"}>{error}</FormControl.Feedback>
      </div>
    </div>
  </Fragment>
}

// Returning: a list of dictionaries for the modifications to the database that
// should be sent, and the list of input serials that was skipped any reason
const BatchEditParse = (formCommon, formTable, bpList) => {
  const modRequests = {}; // List of modifications to be carried out
  const skipEntries = {}; // List of modifications that should be skipped for any reason

  formTable.forEach((row, rowIdx) => {
    if (!row.identifier) return; // Skipping over ld entries
    // Creating the common modifications
    const updateForm = {}
    Object.keys(formCommon).forEach((key) => updateForm[key] = formCommon[key][0]);

    // Looping over the table entries
    let bp_error = ""; // Any error message that needs to be carried over
    for (const key of Object.keys(row)) {
      if (row[key] === null || row[key] === undefined) { bp_error = `Has empty entry at column [${key}]`; break; }
      if (key !== "identifier") { updateForm[key] = row[key] }
    }
    // Making sure table exists
    const bp = findTableRow(bpList, (bp) => Number(bp.workshop_serial) === Number(row.identifier) || bp.bare_barcode === row.identifier);
    if (!bp) bp_error = "Baseplate not found!"

    if (bp && bp_error === "") {
      modRequests[row.identifier] = [bp.id, updateForm];
    } else {
      skipEntries[row.identifier] = bp_error;
    }
  });
  return [modRequests, skipEntries];
}

//
const defaultBatchInvalid = (errorCommon, tableError) => {
  const inval = defaultValidateAdd(errorCommon);
  if (inval !== "") { return inval; }
  for (let idx = 0; idx < tableError.length; ++idx) {
    const row = tableError[idx];
    for (const key of Object.keys(row)) {
      if (row[key] !== "" && row[key] !== undefined) {
        return `Table contains error! (${row[key]})`
      }
    }
  }
  return ""
}

// Returning a list of actions to take for various items
const SortActionList = (actionList, modRequests) => {
  const newIdList = actionList.map(() => []);
  for (const identifier of Object.keys(modRequests)) {
    const [id, modForm] = modRequests[identifier]
    for (let actionIdx = 0; actionIdx < actionList.length; ++actionIdx) {
      if (actionList[actionIdx].parse(modForm, id)) {
        newIdList[actionIdx].push(identifier);
        break;
      }
    }
  }
  for (let actionIdx = 0; actionIdx < actionList.length; ++actionIdx) {
    actionList[actionIdx].idList[1](newIdList[actionIdx]);
  }
}

const BatchEditLamination = ({ showState }) => {
  const [show, setShow] = showState;
  const [showAction, setShowAction] = useState(false);
  const [running, setRunning] = useState(false);
  const { baseplateList, kaptonList, glueList, userList, updateBaseplateList } = useContext(DatabaseContext);

  const formCommon = {
    "kapton_id": useState(null),
    "glue_id": useState(null),
    "glue_mass": useState(null),
    "lam_date": useState(new Date()),
    "lam_user": useState(null),
    "lam_user2": useState(null),
    "inspect_date": useState(new Date()),
    "inspect_user1": useState(null),
    "inspect_user2": useState(null),
  }
  // eslint-disable-next-line
  const errorCommon = {
    "kapton_id": useState(""),
    "glue_id": useState(""),
    "glue_mass": useState(""),
    "lam_user": useState(""),
    "inspect_user1": useState("")
  }

  const ColumnFormat = [
    { title: "BASEPLATE ID", target: "identifier", type: "text", validate: validateIdentifier(baseplateList) },
    { title: "WEIGHT LAM. [g]", target: "weight_lam", type: "text", validate: validateDecimal() },
    { title: "GLUE IN HOLE", target: "defect_lam_glue_hole", type: "text", validate: validateNonNegativeInt() },
    { title: "GLUE ON PAD", target: "defect_lam_glue_pad", type: "text", validate: validateNonNegativeInt() },
    { title: "DELAM. PAD", target: "defect_lam_delam_pad", type: "text", validate: validateNonNegativeInt() },
    { title: "KAPTON ALIGN", target: "kapton_align_passed", type: "bool", },
  ]
  const tabDataState = useState(createDefaultTable(ColumnFormat, 15, null));
  const tabErrState = useState(createDefaultTable(ColumnFormat, 15, ""));

  // Actions to take after the the computing the results
  const actionList = [
    { "idList": useState([]), "headStyle": { color: "red" }, "parse": (form) => form.kapton_align_passed === false, "description": "Not Aligned! Strike from worksheet and Move to \"DELAM\" box (R224)" },
    {
      "idList": useState([]), "headStyle": { color: "orange" },
      "parse": (form) => Number(form.defect_lam_delam_pad) !== 0 || Number(form.defect_lam_glue_pad) !== 0 || Number(form.defect_lam_glue_hole) !== 0,
      "description": "Defects! Strike from worksheet and Move to \"RECOVERY\" box (cabinet 3.b)"
    },
    { "idList": useState([]), "headStyle": { color: "darkgreen" }, "parse": () => true, "description": "Add good! Move to \"LAMINATED\" box at Keyence Station" }
  ]
  const [skip, setSkip] = useState({});

  const [invalid, setInvalid] = useState("");
  useEffect(() => { setInvalid(defaultBatchInvalid(errorCommon, tabErrState[0])); }, [errorCommon, tabErrState]);

  const RunSave = async () => {
    setRunning(true);
    const [modRequests, skipEntries] = BatchEditParse(formCommon, tabDataState[0], baseplateList);
    for (const identifier of Object.keys(modRequests)) { // Updating the baseplate information
      const [bp_id, modForm] = modRequests[identifier];
      const bp = findTableRow(baseplateList, (bp) => Number(bp.id) === Number(bp_id));
      const kap = findTableRow(kaptonList, (kap) => kap.id === formCommon["kapton_id"][0])
      if (typeof (bp.type) === "string" && bp.type[0] === "S") { // Updating type when breaking symmetry of plates
        modForm['type'] = kap.type
      }
      if (modForm["glue_id"] === 3 || modForm["glue_id"] === 4) {
        modForm["surface_preptime"] = 2400; // Pin
      }
      updateBP(bp_id, modForm);
    }
    setRunning(false);
    await updateBaseplateList();

    // Post Actions
    setSkip(skipEntries);
    ResetFormCommon(formCommon)
    ResetTabContent(tabDataState, ColumnFormat, null)
    SortActionList(actionList, modRequests);
    setShow(false);
    setShowAction(true);
  }

  return <Fragment>
    <Modal show={show} onHide={() => setShow(false)} animation="false" dialogClassName="modal-xxl" className="modal-xxl-container">
      <Modal.Header closeButton>
        <Modal.Title>Report lamination result</Modal.Title><br />
      </Modal.Header>
      <Modal.Body>
        <HGCFormRowSelect label="KAPTON" dataState={formCommon["kapton_id"]} errorState={errorCommon["kapton_id"]} options={makeKaptonOptions(kaptonList, null, true)} allowNull={false} autoFocus={true} />
        <HGCFormRowSelect label="GLUE ID" dataState={formCommon["glue_id"]} errorState={errorCommon["glue_id"]} options={makeGlueOptions(glueList, true)} allowNull={false} />
        <HGCFormRowText label="GLUE MASS" dataState={formCommon["glue_mass"]} errorState={errorCommon["glue_id"]} allowNull={false} validate={validateDecimal()} />
        <HGCFormRowDate label="LAMINATE DATE" dataState={formCommon["lam_date"]} />
        <HGCFormRowSelect label="LAMINATE USER (1)" dataState={formCommon["lam_user"]} errorState={errorCommon["lam_user"]} options={userOptions(userList)} allowNull={false} />
        <HGCFormRowSelect label="LAMINATE USER (2)" dataState={formCommon["lam_user2"]} options={userOptions(userList)} />
        <HGCFormRowDate label="INSPECT DATE" dataState={formCommon["inspect_date"]} />
        <HGCFormRowSelect label="INSPECT USER (1)" dataState={formCommon["inspect_user1"]} errorState={errorCommon["inspect_user1"]} options={userOptions(userList)} allowNull={false} />
        <HGCFormRowSelect label="INSPECT USER (2)" dataState={formCommon["inspect_user2"]} options={userOptions(userList)} />
        <br />
        <BatchTable header={ColumnFormat} dataState={tabDataState} errorState={tabErrState} />
        <br />
      </Modal.Body>
      <Modal.Footer>
        <BatchEditButton action={RunSave} invalid={invalid} running={running} />
      </Modal.Footer>
    </Modal>
    <BatchEditActions title="Laminate result reported" showState={[showAction, setShowAction]} actionList={actionList} skipEntries={skip} />
  </Fragment >
}


const BatchEditTape = ({ showState }) => {
  const [show, setShow] = showState;
  const [showAction, setShowAction] = useState(false);
  const [running, setRunning] = useState(false);
  const { baseplateList, tapeList, userList, updateBaseplateList, updateTapeList } = useContext(DatabaseContext);

  const formCommon = {
    "tape_id": useState(null),
    "tape_date": useState(new Date()),
    "tape_user1": useState(null),
    "tape_user2": useState(null),
  }
  // eslint-disable-next-line
  const errorCommon = {
    "tape_id": useState(""),
    "tape_user1": useState("")
  }
  // Additional import for reporting how many times we found tape failures
  const [failCount, setFailCount] = useState(0);
  const [errorCount, setErrorCount] = useState("");

  // Main batch editing table
  const ColumnFormat = [
    { title: "BASEPLATE ID", target: "identifier", type: "text", validate: validateIdentifier(baseplateList) },
    { title: "CMS BARCODE", target: "bar_code", type: "text", validate: validateCMSbarcode(null, baseplateList) }
  ];
  const tabDataState = useState(createDefaultTable(ColumnFormat, 15, null));
  const tabErrState = useState(createDefaultTable(ColumnFormat, 15, ""));

  const bpMiss = (form, id) => {
    const bp = findByUnique(baseplateList, 'id', id)
    return (
      ((bp.weight_bare === null && bp.thickness === null) || bp.weight_lam === null)
      || (bp.radius0 === null)
      || (bp.flatness_bare_l === null && bp.flatness_bare_r === null)
      || (bp.flatness_lam === null || bp.height_lam0 === null)
      || (bp.kapton_id === null)
    )
  }

  const bpBad = (form, id) => {
    const bp = findByUnique(baseplateList, 'id', id)
    return (
      (bp.flatness_grade === "red" || bp.tolerance_grade === "red")
      || (bp.kapton_align_passed !== true)
      || (Number(bp.defect_lam_delam_pad) !== 0 || Number(bp.defect_lam_glue_hole) !== 0 || Number(bp.defect_lam_glue_pad) !== 0)
    );
  }


  // For displaying additional actions for what needs to be done.
  const actionList = [
    { "idList": useState([]), "headStyle": { color: "red" }, "parse": bpBad, "description": "Baseplate has \"RED\" grade quality! Take this plate to Yi-Mu (R224)" },
    { "idList": useState([]), "headStyle": { color: "orange" }, "parse": bpMiss, "description": "Baseplate has missing information! Take this plate to Yi-Mu (R224)" },
    { "idList": useState([]), "headStyle": { color: "darkgreen" }, "parse": () => true, "description": "All good! Move to \"Taped\" box (cabinet 3.a)" }
  ]
  const [skip, setSkip] = useState({});

  // For blocking the run button if known erros exist
  const [invalid, setInvalid] = useState("");
  useEffect(() => {
    setInvalid(defaultBatchInvalid(errorCommon, tabErrState[0]));
  }, [errorCommon, tabErrState]);

  // Code to execute when the run buttong is hit
  const RunSave = async () => {
    setRunning(true);
    const [modRequests, skipEntries] = BatchEditParse(formCommon, tabDataState[0], baseplateList);
    for (const identifier of Object.keys(modRequests)) { // Updating the baseplate information
      const [bp_id, modForm] = modRequests[identifier];
      modForm["type"] = modForm["bar_code"].substring(5, 8);
      updateBP(bp_id, modForm);
    }
    // Update number of defective tape
    const tape = findTableRow(tapeList, (t) => t.id === formCommon["tape_id"][0]);
    updateTape(tape.id, { 'defective_count': Number(tape.defective_count) + Number(failCount) });
    await updateBaseplateList();
    await updateTapeList();
    setRunning(false); // Finished updating the database

    // Post Actions
    setSkip(skipEntries);
    ResetFormCommon(formCommon)
    ResetTabContent(tabDataState, ColumnFormat, null)
    SortActionList(actionList, modRequests);
    setShow(false);
    setShowAction(true);
  }

  return <Fragment>
    <Modal show={show} onHide={() => setShow(false)} animation="false" size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Report Tape Result</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <HGCFormRowSelect label="TAPE" dataState={formCommon["tape_id"]} errorState={errorCommon["tape_id"]} options={makeTapeOptions(tapeList, null, true)} allowNull={false} autoFocus={true} />
        <HGCFormRowDate label="TAPE DATE" dataState={formCommon["tape_date"]} />
        <HGCFormRowSelect label="TAPE USER (1)" dataState={formCommon["tape_user1"]} errorState={errorCommon["tape_user1"]} options={userOptions(userList)} allowNull={false} />
        <HGCFormRowSelect label="TAPE USER (2)" dataState={formCommon["tape_user2"]} options={userOptions(userList)} />
        <HGCFormRowText label="FAIL COUNT" dataState={[failCount, setFailCount]} errorState={[errorCount, setErrorCount]} validate={validateNonNegativeInt(true)} />
        <br />
        <BatchTable header={ColumnFormat} dataState={tabDataState} errorState={tabErrState} />
        <br />
      </Modal.Body>
      <Modal.Footer>
        <BatchEditButton invalid={invalid} action={RunSave} running={running} />
      </Modal.Footer>
    </Modal>
    <BatchEditActions title="Tape reported" showState={[showAction, setShowAction]} actionList={actionList} skipEntries={skip} />
  </Fragment >
}

const BatchEditRecovery = ({ showState }) => {
  const [show, setShow] = showState;
  const [showAction, setShowAction] = useState(false);
  const [running, setRunning] = useState(false);
  const { baseplateList, userList, updateBaseplateList } = useContext(DatabaseContext);

  const formCommon = {
    "recovery_date": useState(new Date()),
    "recovery_user1": useState(null),
    "recovery_user2": useState(null),
  }
  // eslint-disable-next-line
  const errorCommon = {
    "recovery_user1": useState(""),
  }

  const ColumnFormat = [
    { title: "BASEPLATE ID", target: "identifier", type: "text", validate: validateIdentifier(baseplateList) },
    { title: "WEIGHT LAM. [g]", target: "weight_lam", type: "text", validate: validateDecimal() },
    { title: "GLUE IN HOLE", target: "defect_lam_glue_hole", type: "text", validate: validateNonNegativeInt() },
    { title: "GLUE ON PAD", target: "defect_lam_glue_pad", type: "text", validate: validateNonNegativeInt() },
    { title: "DELAM. PAD", target: "defect_lam_delam_pad", type: "text", validate: validateNonNegativeInt() },
    { title: "KAPTON ALIGN", target: "kapton_align_passed", type: "bool", },
  ]
  const tabDataState = useState(createDefaultTable(ColumnFormat, 15, null));
  const tabErrState = useState(createDefaultTable(ColumnFormat, 15, ""));

  // Actions to take after the the computing the results
  const actionList = [
    { "idList": useState([]), "headStyle": { color: "red" }, "parse": (form) => form.kapton_align_passed === false, "description": "Not Aligned! Strike from worksheet and Move to \"DELAM\" box (cabinet 3.b)" },
    {
      "idList": useState([]), "headStyle": { color: "orange" },
      "parse": (form) => Number(form.defect_lam_delam_pad) !== 0 || Number(form.defect_lam_glue_pad) !== 0 || Number(form.defect_lam_glue_hole) !== 0,
      "description": "Defects! Strike from worksheet and Move to \"RECOVERY\" box (cabinet 3.b)"
    },
    { "idList": useState([]), "headStyle": { color: "darkgreen" }, "parse": () => true, "description": "Add good! Move to \"LAMINATED\" box at Keyence Station" }
  ]
  const [skip, setSkip] = useState({});

  const [invalid, setInvalid] = useState("");
  useEffect(() => { setInvalid(defaultBatchInvalid(errorCommon, tabErrState[0])); }, [errorCommon, tabErrState]);

  const RunSave = async () => {
    setRunning(true);
    const [modRequests, skipEntries] = BatchEditParse(formCommon, tabDataState[0], baseplateList);
    for (const identifier of Object.keys(modRequests)) { // Updating the baseplate information
      const [bp_id, modForm] = modRequests[identifier];
      updateBP(bp_id, modForm);
    }
    setRunning(false);
    await updateBaseplateList();

    // Post Actions
    setSkip(skipEntries);
    ResetFormCommon(formCommon)
    ResetTabContent(tabDataState, ColumnFormat, null)
    SortActionList(actionList, modRequests);
    setShow(false);
    setShowAction(true);
  }

  return <Fragment>
    <Modal show={show} onHide={() => setShow(false)} animation="false" dialogClassName="modal-xxl" className='modal-xxl-container'>
      <Modal.Header closeButton>
        <Modal.Title>Report Recovery Results</Modal.Title><br />
      </Modal.Header>
      <Modal.Body>
        <HGCFormRowDate label="RECOVERY DATE" dataState={formCommon["recovery_date"]} />
        <HGCFormRowSelect label="RECOVERY USER (1)" dataState={formCommon["recovery_user1"]} errorState={errorCommon["recovery_user1"]} options={userOptions(userList)} allowNull={false} />
        <HGCFormRowSelect label="RECOVERY USER (2)" dataState={formCommon["recovery_user2"]} options={userOptions(userList)} />
        <br />
        <BatchTable header={ColumnFormat} dataState={tabDataState} errorState={tabErrState} />
      </Modal.Body>
      <Modal.Footer>
        <BatchEditButton invalid={invalid} action={RunSave} running={running} />
      </Modal.Footer>
    </Modal>
    <BatchEditActions title="Tape reported" showState={[showAction, setShowAction]} actionList={actionList} skipEntries={skip} />
  </Fragment >
}

const BatchEditDelam = ({ showState }) => {
  const TAB_LENGTH = 25
  const [show, setShow] = showState;
  const [showAction, setShowAction] = useState(false);
  const [running, setRunning] = useState(false);
  const { baseplateList, kaptonList, tapeList, updateKaptonList, updateBaseplateList, updateTapeList } = useContext(DatabaseContext);
  const formCommon = {
    // Items that needs to be set, but these will not be exposed to the user
    "kapton_id": useState(null),
    "kapton_align_passed": useState(null),
    "weight_lam": useState(null),
    "defect_lam_glue_hole": useState(null),
    "defect_lam_glue_pad": useState(null),
    "defect_lam_delam_pad": useState(null),
    "lam_date": useState(null),
    "lam_user": useState(null),
    "lam_user2": useState(null),
    "tape_id": useState(null),
    "tape_date": useState(null),
    "tape_user1": useState(null),
    "tape_user2": useState(null),
    "bar_code": useState(null),
  }

  const ColumnFormat = [
    { title: "BASEPLATE ID", target: "identifier", type: "text", validate: validateIdentifier(baseplateList) },
    { title: "WEIGHT BARE [g]", target: "weight_bare", type: "text", validate: validateDecimal() }
  ];
  const tabDataState = useState(createDefaultTable(ColumnFormat, TAB_LENGTH, null));
  const tabErrState = useState(createDefaultTable(ColumnFormat, TAB_LENGTH, ""));

  // For displaying additional actions for what needs to be done.
  const actionList = [
    { "idList": useState([]), "headStyle": { color: "green" }, "parse": () => true, "description": "Reported. Move to \"Bare\" box at Keyence station" }
  ]
  const [skip, setSkip] = useState({});

  // For blocking the run button if known errors exist
  const [invalid, setInvalid] = useState("");
  useEffect(() => { setInvalid(defaultBatchInvalid({}, tabErrState[0])); }, [tabErrState]);

  const RunSave = async () => {
    setRunning(true);
    const [modRequests, skipEntries] = BatchEditParse(formCommon, tabDataState[0], baseplateList);
    const kaptonUpdateTally = {}; // items to keep track of how many items needs to be listed as defective
    const tapeUpdateTally = {};
    for (const identifier of Object.keys(modRequests)) { // Updating the counts, listed removed kapton/tape as defective
      const [bp_id, modForm] = modRequests[identifier];
      const bp = findTableRow(baseplateList, (bp) => Number(bp.id) === bp_id);
      const kap = findTableRow(kaptonList, (kap) => kap.id === bp.kapton_id);
      const tape = findTableRow(tapeList, (tape) => tape.id === bp.tape_id);
      if (kap) { kaptonUpdateTally[kap.id] = tapeUpdateTally[kap.id] ? kaptonUpdateTally[kap.id] + 1 : Number(kap.defective_count) + 1; }
      if (tape) { tapeUpdateTally[tape.id] = tapeUpdateTally[tape.id] ? tapeUpdateTally[tape.id] + 1 : Number(tape.defective_count) + 1; }
      updateBP(bp_id, modForm); // Running the baseplate update
    }
    for (const kap_id in kaptonUpdateTally) { updateKapton(kap_id, { "defective_count": Number(kaptonUpdateTally[kap_id]) }); }
    for (const tape_id in tapeUpdateTally) { updateTape(tape_id, { "defective_count": Number(tapeUpdateTally[tape_id]) }); }
    setRunning(false);

    // Post Actions
    setSkip(skipEntries);
    SortActionList(actionList, modRequests);
    ResetFormCommon(formCommon);
    ResetTabContent(tabDataState, ColumnFormat, null)
    setShow(false);
    setShowAction(true);
    // Pulling update to all modified tables
    await updateBaseplateList();
    await updateKaptonList();
    await updateTapeList();
  }

  return <Fragment>
    <Modal show={show} onHide={() => setShow(false)} animation="false" size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Report Delamination Results</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <BatchTable header={ColumnFormat} dataState={tabDataState} errorState={tabErrState} />
      </Modal.Body>
      <Modal.Footer>
        <BatchEditButton invalid={invalid} action={RunSave} running={running} />
      </Modal.Footer>
    </Modal>
    <BatchEditActions title="Delamination reported" showState={[showAction, setShowAction]} actionList={actionList} skipEntries={skip} />
  </Fragment >
}

export const BaseplateList = () => {
  const { baseplateList } = useContext(DatabaseContext);
  const [showAdd, setShowAdd] = useState(false);
  const [showMultiAdd, setShowMultiAdd] = useState(false);
  const [showLam, setShowLam] = useState(false);
  const [showTape, setShowTape] = useState(false);
  const [showRec, setShowRec] = useState(false);
  const [showDelam, setShowDelam] = useState(false);

  // eslint-disable-next-line
  const handleKeyPress = (event) => {
    const noEdit = !(showAdd || showMultiAdd || showLam || showTape || showRec || showDelam);
    if (noEdit && event.altKey) {
      if (event.key === "1") setShowAdd(true);
      else if (event.key === "2") setShowMultiAdd(true);
      else if (event.key === "3") setShowLam(true);
      else if (event.key === "4") setShowTape(true);
      else if (event.key === "5") setShowRec(true);
      else if (event.key === "6") setShowDelam(true);
    }
  }
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    };
  }, [handleKeyPress])

  return <ProtectedComponent>
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <div>
        <Button onClick={() => { setShowAdd(true) }} style={{ marginRight: "3px" }}>Add baseplate <sup>(Alt+1)</sup></Button>
        <Button onClick={() => { setShowMultiAdd(true) }} style={{ marginRight: "15px" }}>Batch Add <sup>(Alt+2)</sup></Button>
        <Button variant="warning" onClick={() => { setShowLam(true) }} style={{ marginRight: "3px" }}>Report Lamination <sup>(Alt+3)</sup></Button>
        <Button variant="warning" onClick={() => { setShowTape(true) }} style={{ marginRight: "15px" }}>Report Tape <sup>(Alt+4)</sup></Button>
        <Button variant="danger" onClick={() => { setShowRec(true) }} style={{ marginRight: "3px" }}>⚠️ Report Recovery <sup>(Alt+5)</sup></Button>
        <Button variant="danger" onClick={() => { setShowDelam(true) }} style={{ marginRight: "3px" }}>⚠️ Report Delamination <sup>(Alt+6)</sup></Button>
      </div> <div style={{ justifyContent: "right" }}>
        <BaseplateQuickCheck />
      </div>
    </div>
    <BaseplateAdd showState={[showAdd, setShowAdd]} />
    <BaseplateMultiAdd showState={[showMultiAdd, setShowMultiAdd]} />
    <BatchEditLamination showState={[showLam, setShowLam]} />
    <BatchEditTape showState={[showTape, setShowTape]} />
    <BatchEditRecovery showState={[showRec, setShowRec]} />
    <BatchEditDelam showState={[showDelam, setShowDelam]} />
    <hr />
    <PaginateTable tableData={baseplateList} columnLayout={BaseplateColumns()} />
  </ProtectedComponent>
}

export default BaseplateList;
